import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

/**
 * Chubacca, the portal mascot (Maroun, 24 Sep 2026): thirteen short clips of
 * him in the CHU hoodie, keyed off their blue screen into transparent video
 * with their audio kept, on a random rotation.
 *
 *  - He IDLES (Maroun, 25 Sep 2026: the clips ran far too fast): calm,
 *    glance and look follow each other for an idle spell of 20 to 40
 *    seconds, then ONE random action clip plays, then a new idle spell.
 *  - The first clip is an idle one, MUTED: no browser autoplays sound.
 *  - Clicking him (or Enter/Space: he is a real button) jumps straight to a
 *    random ACTION clip and turns the sound on, because a click is the user gesture
 *    a browser requires before it will play audio.
 *  - Autoplay refused (Safari 27 refused even muted on 24 Sep 2026, then drew
 *    its own play button over him) or reduced motion set: his still shows
 *    instead, and a click plays a clip, since the click is the gesture.
 *
 * Clips change without flicker: the next one loads in a SECOND, hidden
 * video and is shown only once it is playing. Swapping src on a single
 * element blanked him to his poster (the still, a different pose) between
 * clips, which read as flicker on the live v0.58.0.
 *
 * Every clip is ALIGNED onto calm, then seated on the nav bar. The clips are
 * framed at slightly different zooms: registering each clip's first and
 * last frames against calm's (scale and shift searched for the best pixel
 * match on his head, 25 Sep 2026) found look 5.5% larger, grin 4.3%, glance
 * and surprise about 2.5%, the rest within half a percent. Seating by paws
 * (v0.58.1) and then by head-top rows (v0.61.0) each left him visibly
 * resizing at a swap (Maroun, both times). `scale`, `tx`, `ty` undo each
 * clip's framing; calm's own drop of 30 rows then seats them all.
 *
 * His box is fixed at the clips' own 390:360, and every video fills it: a
 * video with no metadata yet is 300x150 by default, so while the next clip
 * loaded his width jumped and he slid left and back (v0.58.1). His still is
 * each clip's own first frame for the same reason: the old still was a
 * different picture, framed larger than the clips.
 *
 * The sound FADES (Maroun, 24 Sep 2026: it was abrupt as he cycled): a clip
 * with sound starts silent and fades in as it is shown, the clip it replaces
 * fades out before it is stopped, and each clip fades out over its last
 * moments rather than stopping dead.
 *
 * TWO ENCODINGS per clip because no single one shows alpha everywhere,
 * measured the same day: Safari draws VP9 WebM with a black box, and
 * Chromium ignores HEVC's alpha layer. The file is chosen per engine.
 * Only the clips actually shown are ever fetched.
 */

export interface ChubaccaClip {
  slug: string
  webm: string
  hevc: string
  /** An idle clip (breathing, looking around), played between actions. */
  idle: boolean
  /** The clip's first frame with its alpha: poster, and the still. */
  poster: string
  /** Scale onto calm's framing (about the frame's top-left corner). */
  scale: number
  /** Shift after scaling, % of the frame's width and height, including the
   *  common seat on the bar. */
  tx: number
  ty: number
}

/** Idle clips: him breathing and looking around, looped between actions. */
const IDLE_SLUGS = new Set(['calm', 'glance', 'look'])
const IDLE_MIN_MS = 20_000
const IDLE_MAX_MS = 40_000

const FRAME_COLS = 390
const FRAME_ROWS = 360
/** [scale, tx px, ty px] mapping each clip onto calm: calm(p) = s * clip(p) + t,
 *  the mean of the first- and last-frame registrations (25 Sep 2026). */
const ALIGN: Record<string, [number, number, number]> = {
  beer: [1, 0, 0],
  calm: [1, 0, 0],
  drums: [0.9975, 0.5, 0.5],
  glance: [0.975, -0.5, -3.5],
  grin: [0.9575, 4, -0.5],
  laugh: [1, 0, 0],
  look: [0.945, 6, 4],
  ponder: [1, 0, 0],
  surprise: [0.97625, 0.5, -5],
  taps: [1, 0, 0],
  wind: [1, 0, 0],
  wink: [1, 0, 0],
  yawn: [0.9875, 2, -0.5],
}
/** calm's paws rest on row 330: every aligned clip drops by the same 30. */
const SEAT_DROP = FRAME_ROWS - 330

const WEBM = import.meta.glob('../assets/chubacca/*.webm', { eager: true, query: '?url', import: 'default' }) as Record<
  string,
  string
>
const HEVC = import.meta.glob('../assets/chubacca/*-hevc.mp4', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const POSTER = import.meta.glob('../assets/chubacca/*-poster.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const byName = (files: Record<string, string>, name: string) =>
  Object.entries(files).find(([p]) => p.endsWith(`/${name}`))?.[1] ?? ''

export const CHUBACCA_CLIPS: ChubaccaClip[] = Object.keys(WEBM)
  .sort()
  .map((path) => {
    const slug = path.replace(/^.*\//, '').replace(/\.webm$/, '')
    return {
      slug,
      webm: WEBM[path]!,
      hevc: byName(HEVC, `${slug}-hevc.mp4`),
      poster: byName(POSTER, `${slug}-poster.webp`),
      scale: ALIGN[slug]?.[0] ?? 1,
      tx: ((ALIGN[slug]?.[1] ?? 0) / FRAME_COLS) * 100,
      ty: (((ALIGN[slug]?.[2] ?? 0) + SEAT_DROP) / FRAME_ROWS) * 100,
      idle: IDLE_SLUGS.has(slug),
    }
  })

function isSafari(): boolean {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  return /safari/i.test(ua) && !/chrome|chromium|crios|fxios|edg|android/i.test(ua)
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch {
    return false
  }
}

/** Every layer fills the fixed box, scaled and shifted onto calm's framing
 *  and seated on the bar (see `scale`, `tx`, `ty`). */
function layer(clip: ChubaccaClip): CSSProperties {
  return {
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transformOrigin: '0 0',
    transform: `translate(${+clip.tx.toFixed(2)}%, ${+clip.ty.toFixed(2)}%) scale(${clip.scale})`,
  }
}

const IDLE_POOL = CHUBACCA_CLIPS.map((c, i) => (c.idle ? i : -1)).filter((i) => i >= 0)
const ACTION_POOL = CHUBACCA_CLIPS.map((c, i) => (c.idle ? -1 : i)).filter((i) => i >= 0)

/** A random clip index from `pool`, never `avoid` when there is any alternative. */
function pickFrom(pool: number[], random: () => number, avoid: number | null): number {
  const choices = pool.length > 1 ? pool.filter((i) => i !== avoid) : pool
  return choices[Math.min(choices.length - 1, Math.floor(random() * choices.length))]!
}

/** How long the next idle spell lasts. */
function idleSpell(random: () => number): number {
  return IDLE_MIN_MS + random() * (IDLE_MAX_MS - IDLE_MIN_MS)
}

const FADE_MS = 400
const FADE_STEP_MS = 20
const fades = new WeakMap<HTMLMediaElement, ReturnType<typeof setInterval>>()

/** Stop any fade on `v`, including a fade-out that would pause it: a slot
 *  handed a new clip must not be stopped by its previous clip's fade. */
function cancelFade(v: HTMLMediaElement) {
  const running = fades.get(v)
  if (running !== undefined) clearInterval(running)
  fades.delete(v)
}

/** Ramp `v.volume` to `to` over FADE_MS in fixed steps (deterministic under
 *  fake timers), replacing any fade already running on that element. */
function fade(v: HTMLMediaElement, to: number, done?: () => void) {
  const running = fades.get(v)
  if (running !== undefined) clearInterval(running)
  const from = v.volume
  const steps = Math.max(1, Math.round(FADE_MS / FADE_STEP_MS))
  let n = 0
  const id = setInterval(() => {
    n += 1
    v.volume = Math.min(1, Math.max(0, from + ((to - from) * n) / steps))
    if (n >= steps) {
      clearInterval(id)
      fades.delete(v)
      done?.()
    }
  }, FADE_STEP_MS)
  fades.set(v, id)
}

/** AbortError is NOT a refusal: it is a pending play() cut off by the next
 *  load (Chrome, 24 Sep 2026). Treating it as one froze him on the still
 *  whenever he was clicked mid-load. */
function refused(e: unknown): boolean {
  return !(e instanceof DOMException && e.name === 'AbortError')
}

type Slot = 0 | 1
type Slots = [number | null, number | null]
/** A request to start the clip in `slot`; `n` makes every request distinct. */
interface Start {
  slot: Slot
  n: number
}

export function ChubaccaMascot({
  style,
  random = Math.random,
  now = Date.now,
  soundAllowed = true,
}: {
  style: CSSProperties
  random?: () => number
  now?: () => number
  /** The admin console's "Chubacca's sound" switch; off keeps every clip
   *  muted, clicks included. */
  soundAllowed?: boolean
}) {
  // He opens idling, and the first idle spell is timed from the start.
  const [initial] = useState(() => {
    const first = pickFrom(IDLE_POOL, random, null)
    return { first, idleUntil: now() + idleSpell(random) }
  })
  const [slots, setSlots] = useState<Slots>(() => [initial.first, null])
  /** Until when he idles; past it, the next clip is an action. */
  const idleUntil = useRef(initial.idleUntil)
  const [front, setFront] = useState<Slot>(0)
  const [sound, setSound] = useState(false)
  const [still, setStill] = useState(() => prefersReducedMotion())
  const [start, setStart] = useState<Start>({ slot: 0, n: 0 })
  const videos = useRef<[HTMLVideoElement | null, HTMLVideoElement | null]>([null, null])

  // Play whichever slot was last asked for. A refusal falls back to the
  // still: with sound it first retries muted, since a browser may allow
  // silent playback where it refuses audio.
  useEffect(() => {
    const v = videos.current[start.slot]
    if (still || !v) return
    cancelFade(v)
    v.muted = !sound
    // With sound it starts silent and fades in once it is shown (onPlaying).
    v.volume = sound ? 0 : 1
    try {
      // A slot can be handed the clip it already holds, paused mid-way.
      v.currentTime = 0
    } catch {
      // not seekable yet: a fresh load starts at 0 anyway
    }
    let result: Promise<void> | undefined
    try {
      result = v.play()
    } catch (e) {
      if (refused(e)) setStill(true)
      return
    }
    result?.catch((e) => {
      if (!refused(e)) return
      if (sound) {
        v.muted = true
        v.play()?.catch((e2) => {
          if (refused(e2)) setStill(true)
        })
      } else {
        setStill(true)
      }
    })
    // `sound` is read when a start is requested, not followed after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, still])

  /** Load the next clip into the hidden slot and start it there: an idle
   *  one during an idle spell, otherwise (or when `action`) an action. */
  function advance(action = false) {
    const back: Slot = front === 0 ? 1 : 0
    const pool = action || now() >= idleUntil.current ? ACTION_POOL : IDLE_POOL
    const next = pickFrom(pool, random, slots[front])
    setSlots((s) => (back === 0 ? [next, s[1]] : [s[0], next]))
    setStart((s) => ({ slot: back, n: s.n + 1 }))
  }

  /** The hidden slot is playing: show it, fade its sound in, and fade out
   *  the one it replaces before stopping it. */
  function onPlaying(slot: Slot) {
    const v = videos.current[slot]
    if (slot === front) {
      // A clip started in the shown slot (after the still): just fade in.
      if (v && sound && v.volume < 1) fade(v, 1)
      return
    }
    const swap = () => {
      setFront(slot)
      const old = videos.current[slot === 0 ? 1 : 0]
      if (v && sound) fade(v, 1)
      if (old) fade(old, 0, () => old.pause())
    }
    // Swap on the first PRESENTED frame where the browser can say so;
    // 'playing' alone can fire a frame before anything is drawn.
    if (v && 'requestVideoFrameCallback' in v) v.requestVideoFrameCallback(() => swap())
    else swap()
  }

  /** Fade the shown clip out over its last moments, not stop dead. */
  function onTimeUpdate(slot: Slot) {
    const v = videos.current[slot]
    if (!v || !sound || slot !== front || fades.has(v) || v.volume === 0) return
    if (v.duration && v.duration - v.currentTime <= FADE_MS / 1000) fade(v, 0)
  }

  function onEnded(slot: Slot) {
    // Only the shown clip rotates him, and not while a click's clip loads.
    if (slot !== front || start.slot !== front) return
    // An action just finished: a fresh idle spell starts now.
    const ended = slots[slot]
    if (ended !== null && !CHUBACCA_CLIPS[ended]?.idle) idleUntil.current = now() + idleSpell(random)
    advance()
  }

  function onClick() {
    setSound(soundAllowed)
    if (still) {
      setStill(false)
      setSlots([pickFrom(ACTION_POOL, random, slots[front]), null])
      setFront(0)
      setStart((s) => ({ slot: 0, n: s.n + 1 }))
      return
    }
    advance(true)
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Chubacca: click for another one"
      title="Click me"
      style={{
        position: 'relative',
        ...style,
        aspectRatio: `${FRAME_COLS} / ${FRAME_ROWS}`,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        lineHeight: 0,
      }}
    >
      {still ? (
        <img src={CHUBACCA_CLIPS[slots[front] ?? 0]?.poster} alt="Chubacca" style={layer(CHUBACCA_CLIPS[slots[front] ?? 0]!)} />
      ) : (
        ([0, 1] as const).map((slot) => {
          const index = slots[slot]
          const clip = index === null ? undefined : CHUBACCA_CLIPS[index]
          if (!clip) return null
          const alone = slots[slot === 0 ? 1 : 0] === null
          return (
            <video
              key={slot}
              ref={(el) => {
                videos.current[slot] = el
              }}
              data-showing={String(slot === front)}
              src={isSafari() ? clip.hevc : clip.webm}
              // Only a video with nothing behind it gets the still as poster;
              // a poster on the loading slot is exactly the flash avoided here.
              poster={alone ? clip.poster : undefined}
              muted={!sound}
              autoPlay={alone}
              playsInline
              disablePictureInPicture
              preload="auto"
              onPlaying={() => onPlaying(slot)}
              onEnded={() => onEnded(slot)}
              onTimeUpdate={() => onTimeUpdate(slot)}
              style={{ ...layer(clip), opacity: slot === front ? 1 : 0 }}
            />
          )
        })
      )}
    </button>
  )
}
